import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loading from "../../components/Loading";
import DashboardQueriesLentasCard from "./DashboardQueriesLentasCard";
import { Context } from "../../Reducers/Store";

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [state] = useContext(Context);

  const temPermissao = (codigo) => {
    return state?.profile?.permissoes?.includes(codigo) ?? false;
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API}/dashboard`)
      .then((res) => setDashboard(res.data.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const cards = [
    { 
      title: "Logins SQL Ativos", 
      value: dashboard?.loginsSqlAtivos || 0,
      total: dashboard?.loginsSqlTotal || 0,
      icon: "database",
      color: "bg-emerald-500",
      link: "/controle-usuarios?ativo=true"
    },
    { 
      title: "Logins SQL Inativos", 
      value: (dashboard?.loginsSqlTotal || 0) - (dashboard?.loginsSqlAtivos || 0),
      icon: "database-off",
      color: "bg-slate-400",
      link: "/controle-usuarios?ativo=false"
    },
    { 
      title: "Exceções Hoje", 
      value: dashboard?.excecoesHoje || 0,
      icon: "calendar",
      color: dashboard?.excecoesHoje > 0 ? "bg-amber-500" : "bg-blue-500",
      link: "/excecoes"
    },
    { 
      title: "Usuários Sistema", 
      value: dashboard?.usuariosAtivos || 0,
      subtitle: `${dashboard?.usuariosInativos || 0} inativos`,
      icon: "users",
      color: "bg-violet-500",
      link: "/usuarios"
    },
  ];

  const loginsRecentes = dashboard?.loginsRecentes || [];
  const ultimasExcecoes = dashboard?.ultimasExcecoes || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Dashboard</h1>
        <p className="text-sm text-slate-500">Gerenciador de Permissões SQL Server</p>
      </div>

      {/* Cards principais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div 
            key={card.title} 
            onClick={() => navigate(card.link)}
            className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.title}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className="text-3xl font-bold text-slate-800">{card.value}</p>
                  {card.total !== undefined && card.total > 0 && (
                    <span className="text-sm text-gray-400">/ {card.total}</span>
                  )}
                </div>
                {card.subtitle && (
                  <p className="text-xs text-gray-400 mt-1">{card.subtitle}</p>
                )}
              </div>
              <div className={`p-2.5 rounded-lg ${card.color} group-hover:scale-110 transition`}>
                {card.icon === "database" && (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                )}
                {card.icon === "database-off" && (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                )}
                {card.icon === "calendar" && (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                {card.icon === "users" && (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráfico de barras - Acessos por Tipo */}
      {dashboard?.acessosPorTipo && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Acessos por Tipo</h2>
          <div className="flex items-end gap-8 h-40">
            {Object.entries(dashboard.acessosPorTipo).map(([tipo, quantidade]) => {
              const maxValue = Math.max(...Object.values(dashboard.acessosPorTipo), 1);
              const height = maxValue > 0 ? (quantidade / maxValue) * 100 : 0;
              const colors = { R: "bg-blue-500", W: "bg-amber-500", A: "bg-red-500" };
              const labels = { R: "Leitura", W: "Escrita", A: "Admin" };
              return (
                <div key={tipo} className="flex flex-col items-center flex-1 max-w-24">
                  <span className="text-sm font-bold text-slate-700 mb-1">{quantidade}</span>
                  <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '120px' }}>
                    <div 
                      className={`absolute bottom-0 w-full ${colors[tipo]} rounded-t-lg transition-all duration-500`}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 mt-2 font-medium">{labels[tipo]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid de 2 ou 3 colunas dependendo da permissão */}
      <div className={`grid grid-cols-1 ${temPermissao("monitoramento.visualizar") ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6`}>
        {/* Logins SQL recentes */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Últimos Logins SQL Criados</h2>
            <button 
              onClick={() => navigate('/controle-usuarios')}
              className="text-xs text-blue-600 hover:underline"
            >
              Ver todos
            </button>
          </div>
          {loginsRecentes.length > 0 ? (
            <div className="space-y-3">
              {loginsRecentes.map((login) => (
                <div 
                  key={login.id} 
                  onClick={() => navigate(`/controle-usuarios/${login.id}`)}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${login.ativo ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                    <svg className={`w-5 h-5 ${login.ativo ? 'text-emerald-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{login.loginName}</p>
                    <p className="text-xs text-gray-500">
                      {login.usuarioWebNome || 'Sem usuário web'} • {login.totalBancos} banco(s)
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${login.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                    {login.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-8">Nenhum login cadastrado</p>
          )}
        </div>

        {/* Últimas exceções */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Últimas Exceções</h2>
            <button 
              onClick={() => navigate('/excecoes')}
              className="text-xs text-blue-600 hover:underline"
            >
              Ver todas
            </button>
          </div>
          {ultimasExcecoes.length > 0 ? (
            <div className="space-y-3">
              {ultimasExcecoes.map((exc) => (
                <div key={exc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${exc.ativo ? 'bg-amber-100' : 'bg-gray-200'}`}>
                    <svg className={`w-5 h-5 ${exc.ativo ? 'text-amber-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{exc.loginName}</p>
                    <p className="text-xs text-gray-500 truncate" title={exc.motivo}>
                      {exc.motivo || 'Sem motivo especificado'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-slate-700">{exc.dataFormatada}</p>
                    <p className="text-xs text-gray-500">{exc.horaInicio} - {exc.horaFim}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-8">Nenhuma exceção cadastrada</p>
          )}
        </div>

        {/* Queries Lentas - só aparece se tiver permissão */}
        {temPermissao("monitoramento.visualizar") && (
          <DashboardQueriesLentasCard />
        )}
      </div>
    </div>
  );
}